import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import debugFn from 'debug';
import qs from 'qs';
import { appendQueryParam } from '../helpers';

const debug = debugFn('api-client');
debug.enabled = false;

interface Args {
  apiRoot: string;
}

export class BaseAPIClient {
  apiRoot: string = null!;
  authToken: string | null = null;
  authAs: string | null = null;
  unauthenticationCb: null | ((err: Error) => any) = null;

  axios: AxiosInstance;

  constructor({ apiRoot }: Args) {
    this.apiRoot = apiRoot;

    this.axios = axios.create({
      baseURL: this.apiRoot,
      paramsSerializer: (params) => {
        return qs.stringify(params, {
          encode: true,
          arrayFormat: 'brackets',
        });
      },
    });
  }

  onUnauthentication(cb: (err: Error) => any) {
    this.unauthenticationCb = cb;
  }

  setToken(authToken: string | null) {
    this.authToken = authToken;
  }

  setAuthAs(authAs: string | null) {
    this.authAs = authAs;
  }

  getToken() {
    return this.authToken;
  }

  request<T>(
    method: Method,
    url: string,
    options: AxiosRequestConfig & {
      noAuth?: boolean;
    } = {},
  ): Promise<AxiosResponse<T>> {
    const { headers: _headers, ...restOptions } = options;

    const headers = _headers || {};

    if (!headers.Authorization && !options.noAuth && this.authToken) {
      headers.Authorization = `Bearer ${this.authToken}`;
    }

    if (this.authAs && !url.startsWith('/admin')) {
      headers['X-Auth-As'] = this.authAs;
    }

    debug(`${method.toUpperCase()} ${url}`);

    return this.axios
      .request<T>({
        method,
        url,
        headers,
        ...restOptions,
      })
      .catch((error) => {
        if (error.response && [401].includes(error.response.status)) {
          if (this.unauthenticationCb) {
            this.unauthenticationCb(error);
          }
        }

        if (error.response?.data) {
          const errorToThrow = {
            ...error.response?.data,
            axiosError: error,
          };

          throw errorToThrow;
        }

        throw error;
      });
  }

  async get<T = any>(url: string, options: AxiosRequestConfig = {}) {
    const { data } = await this.request<T>('get', url, options);

    return data;
  }

  async post<T = any>(url: string, data?: any, options: AxiosRequestConfig = {}) {
    const { data: responseData } = await this.request<T>('post', url, {
      ...options,
      data,
    });

    return responseData;
  }

  async patch<T = any>(url: string, data?: any, options: AxiosRequestConfig = {}) {
    const { data: responseData } = await this.request<T>('patch', url, {
      ...options,
      data,
    });

    return responseData;
  }

  async postFile<T = any>(url: string, file: File, options: AxiosRequestConfig = {}) {
    const data = new FormData();
    data.append('file', file);

    return this.post<T>(url, data, options);
  }

  async put<T = any>(url: string, data?: any, options: AxiosRequestConfig = {}) {
    const { data: responseData } = await this.request<T>('put', url, {
      ...options,
      data,
    });

    return responseData;
  }

  async delete<T = any>(url: string, data?: any, options: AxiosRequestConfig = {}) {
    const { data: responseData } = await this.request<T>('delete', url, {
      ...options,
      data,
    });

    return responseData;
  }

  generateUrl(
    path: string,
    opts: { prependBaseURL?: boolean; secure?: boolean; params?: object } = {},
  ) {
    let url =
      opts.prependBaseURL !== false && !(path.startsWith('https://') || path.startsWith('http://'))
        ? `${this.apiRoot}${path}`
        : path;

    if (opts.secure) {
      url = appendQueryParam(url, 'access_token', this.authToken);

      if (this.authAs) {
        url = appendQueryParam(url, 'auth_as', this.authAs);
      }
    }

    if (opts.params) {
      Object.entries(opts.params).forEach(([key, value]) => {
        url = appendQueryParam(url, key, value);
      });
    }

    return url;
  }

  getApiRoot() {
    return this.apiRoot;
  }
}
